import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';

interface TransferInfo {
  valid: boolean;
  serialNumber: string;
  warrantyRemaining: string;
  expiresAt: string;
}

export const TransferPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [code, setCode] = useState(token || '');
  const [step, setStep] = useState<'enter' | 'confirm' | 'register' | 'success' | 'error'>('enter');
  const [transferInfo, setTransferInfo] = useState<TransferInfo | null>(null);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-validate if token is in URL
  useEffect(() => {
    if (token && token.length === 6) {
      validateToken(token);
    }
  }, [token]);

  async function validateToken(transferToken: string) {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/passkey/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate', transferToken }),
      });
      const data = await res.json();
      if (data.valid) {
        setTransferInfo(data);
        setStep('confirm');
      } else {
        setError(data.error || 'Invalid or expired transfer code');
        setStep('error');
      }
    } catch {
      setError('Could not validate transfer code. Please try again.');
      setStep('error');
    }
    setLoading(false);
  }

  async function completeTransfer() {
    if (!transferInfo || !buyerName || !buyerEmail) return;
    setLoading(true);
    setError('');

    try {
      // Get registration options for the buyer
      const optRes = await fetch('/api/passkey/register-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serialNumber: transferInfo.serialNumber,
          ownerEmail: buyerEmail,
          ownerName: buyerName,
        }),
      });
      const options = await optRes.json();

      if (!options.challengeId) {
        setError('Could not start registration. Please try again.');
        setLoading(false);
        return;
      }

      // Start WebAuthn registration on buyer's device
      const credential = await startRegistration({ optionsJSON: options.options });

      // Complete the transfer
      const completeRes = await fetch('/api/passkey/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'complete',
          transferToken: code,
          buyerEmail,
          buyerName,
          credential,
          challengeId: options.challengeId,
        }),
      });
      const result = await completeRes.json();

      if (result.transferred) {
        setStep('success');
      } else {
        setError(result.error || 'Transfer failed. Please try again.');
        setStep('error');
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey registration was cancelled. Please try again.');
      } else {
        setError(err.message || 'Transfer failed. Please try again.');
      }
      setStep('error');
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Product Ownership Transfer</h1>
          <p className="mt-2 text-sm text-gray-500">
            Accept ownership of a registered product
          </p>
        </div>

        {/* Step 1: Enter transfer code */}
        {step === 'enter' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transfer Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="e.g. XK7M4P"
              maxLength={6}
              className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={() => validateToken(code)}
              disabled={code.length !== 6 || loading}
              className="mt-4 w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50 transition-all"
              style={{ backgroundColor: '#241E61' }}
            >
              {loading ? 'Validating...' : 'Verify Code'}
            </button>
          </div>
        )}

        {/* Step 2: Confirm product details */}
        {step === 'confirm' && transferInfo && (
          <div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Product</h3>
              <p className="mt-1 text-lg font-semibold text-gray-900">{transferInfo.serialNumber}</p>
              <p className="mt-1 text-sm text-gray-600">Warranty: {transferInfo.warrantyRemaining}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <input
                  type="text"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Full name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                <input
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <button
              onClick={completeTransfer}
              disabled={!buyerName || !buyerEmail || loading}
              className="mt-6 w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50 transition-all"
              style={{ backgroundColor: '#241E61' }}
            >
              {loading ? 'Creating passkey...' : 'Accept Ownership (Create Passkey)'}
            </button>

            <p className="mt-3 text-xs text-gray-400 text-center">
              Your device will prompt you to create a passkey to verify your identity as the new owner.
            </p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Ownership Transferred</h2>
            <p className="mt-2 text-sm text-gray-500">
              You are now the registered owner of this product. Your warranty and support access are active.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 w-full py-3 rounded-lg font-semibold text-white transition-all"
              style={{ backgroundColor: '#241E61' }}
            >
              Go to Homepage
            </button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Transfer Failed</h2>
            <p className="mt-2 text-sm text-red-600">{error}</p>
            <button
              onClick={() => { setStep('enter'); setError(''); }}
              className="mt-6 w-full py-3 rounded-lg font-semibold text-white transition-all"
              style={{ backgroundColor: '#241E61' }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
